
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import LockIcon from '@mui/icons-material/Lock';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { ProductCategoryType } from '../../../common/enums';
import { getOrderItemKey, groupStaffOrderItems } from '../../../common/orderFunctions';
import { StaffOrder, StaffOrderItem } from '../../../common/types';
import { getOrderBackgroundColours } from '../functions/orderFunctions';
import {
  allColdDrinksFinished,
  allColdDrinksPlaced,
  allColdDrinksStarted,
  allHotCoffeeOrTeaFinished,
  allHotCoffeeOrTeaPlaced,
  allHotCoffeeOrTeaStarted,
} from '../functions/stationFunctions';
import { IndicatorBox } from './IndicatorBox';
import PaidStatusIcon from './PaidStatusIcon';
import { TimeIndicator } from './TimeIndicator';

interface DraggableFoodOrderCardProps {
  isAllFoodStarted: boolean;
  isAllFoodPlaced: boolean;
  order: StaffOrder;
  isLoading: boolean;
  handleStartOrder: () => Promise<void>;
  handleStopOrder: () => Promise<void>;
  handleFinishOrder: () => Promise<void>;
}

export default function DraggableFoodOrderCard({
  isAllFoodStarted,
  isAllFoodPlaced,
  order,
  isLoading,
  handleStartOrder,
  handleStopOrder,
  handleFinishOrder,
}: DraggableFoodOrderCardProps) {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: order.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { headerBg, bodyBg } = getOrderBackgroundColours(order.isDraft)

  const groupedItems = groupStaffOrderItems((order as StaffOrder).orderItems);

  return (
    <Stack ref={setNodeRef} {...attributes} {...listeners} style={style}>
      <Stack
        sx={{
          backgroundColor: headerBg,
          flexDirection: 'row',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: 0,
        }}
      >
        <Stack
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            marginRight: 'auto'
          }}
        >
          <Stack sx={{ padding: 1, alignItems: 'center' }}>
            {isAllFoodStarted ? <LockIcon /> : <DragHandleIcon />}
          </Stack>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h3" sx={{ marginLeft: theme.spacing(1), color: order.isDraft ? 'black' : theme.palette.primary.main }}>
            {order.customerName}
          </Typography>
        </Stack>
        <PaidStatusIcon isPaid={!!order.isPaid} isDraft={order.isDraft} />
        <TimeIndicator time={order.requestedPickupTime} isDraft={order.isDraft} />
      </Stack>
      <TableContainer component={Paper} sx={[{ backgroundColor: bodyBg }]}>
        <Table sx={{ minWidth: 650 }} size="small" aria-label="order table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '10%' }}>Qty</TableCell>
              <TableCell sx={{ width: '50%' }}>Name</TableCell>
              <TableCell sx={{ width: '40%' }}>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedItems.map((orderItemWithQty) => {
              if (
                orderItemWithQty.productCategoryType !==
                ProductCategoryType.Food
              ) {
                return null;
              }

              return (
                <TableRow key={getOrderItemKey(orderItemWithQty)}>
                  <TableCell>{orderItemWithQty.quantity}</TableCell>
                  <TableCell>{orderItemWithQty.productName}</TableCell>
                  <TableCell sx={{ width: '25em' }}>
                    {orderItemWithQty?.notes || ''}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Stack
            sx={{
              justifyContent: 'flex-start',
              flex: 1,
              flexDirection: 'row',

            }}
          >
            {order.orderItems.some(
              (item: StaffOrderItem) =>
                item.productCategoryType === ProductCategoryType.ColdDrinks,
            ) && (
                <IndicatorBox
                  orderItems={order.orderItems}
                  label="Cold Drinks"
                  allStartedFn={allColdDrinksStarted}
                  allFinishedFn={allColdDrinksFinished}
                  allPendingFn={allColdDrinksPlaced}
                />
              )}
            {order.orderItems.some(
              (item: StaffOrderItem) =>
                item.productCategoryType === ProductCategoryType.HotCoffee ||
                item.productCategoryType === ProductCategoryType.Tea,
            ) && (
                <IndicatorBox
                  orderItems={order.orderItems}
                  label="Hot Coffee"
                  allStartedFn={allHotCoffeeOrTeaStarted}
                  allFinishedFn={allHotCoffeeOrTeaFinished}
                  allPendingFn={allHotCoffeeOrTeaPlaced}
                />
              )}
          </Stack>

          <Stack
            sx={{
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
              flex: 1,
            }}
          >
            {!order.isDraft && isAllFoodPlaced && (
              <Button
                variant="contained"
                size="small"
                sx={{
                  margin: 1,
                  width: '10em',
                }}
                disabled={isLoading}
                onClick={handleStartOrder}
              >
                Start Order
              </Button>
            )}
            {!order.isDraft && isAllFoodStarted && (
              <Stack sx={{ flexDirection: 'row' }}>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  sx={{
                    margin: 1,
                    width: '10em',
                    whiteSpace: 'nowrap',
                  }}
                  disabled={isLoading}
                  onClick={handleStopOrder}
                >
                  Reset Order
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    margin: 1,
                    whiteSpace: 'nowrap',
                  }}
                  disabled={isLoading}
                  onClick={handleFinishOrder}
                >
                  Finish Order
                </Button>
              </Stack>
            )}
          </Stack>
        </Stack>
      </TableContainer>
    </Stack>
  );
}
